import React from "react";
// Meter Header

// Default
import { useState, useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

// Mui
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import useMediaQuery from "@mui/material/useMediaQuery";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";

// Custom
import MeterDetailLeftCard from "./MeterDetailLeftCard";
import CustomToast from "../../components/toast/CustomToast";

// Theme
import theme from "../../theme";

// Custom
import CustomSearch from "../search/Search";

// Redux
import {
  setIsMeterLoading,
  setHasMeterSearched,
  setMeterId,
  setMeterDetails,
  setMeterV2Details,
  setSiteV2Details,
  setContractV2Details,
  setInstantTelemetryV2,
} from "../../redux/features/MeterSlice";
import {
  setCustomerDetails,
  setCustomerId,
  setCustomerV2Details,
} from "../../redux/features/CustomerSlice";

// Utils
import { fetchGETRequest } from "../../utils/services";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { ToastState, InstantTelemetryV2Response } from "../../types";

const MeterHeader: React.FC = () => {
  const matches = useMediaQuery(theme.breakpoints.down("lg"));
  const location = useLocation();
  const [chip, setChip] = useState("Connect");
  const [searchParams] = useSearchParams();
  const [toast, setToast] = useState<ToastState>({
    message: "",
    severity: "",
  });

  useEffect(() => {
    const meteridParam = searchParams.get("meterid");
    if (meteridParam) {
      dispatch(setHasMeterSearched(true));
      dispatch(setMeterId(meteridParam));
      handleSearch(meteridParam);
    } else if (location?.state) {
      dispatch(setHasMeterSearched(true));
      handleSearch(location.state);
    } else {
      dispatch(setHasMeterSearched(false));
      dispatch(setMeterId(""));
    }
  }, [location, searchParams]);

  const { hasMeterSearched, isMeterLoading, meterId } = useAppSelector(
    (state) => state.meter
  );
  const dispatch = useAppDispatch();

  const getChipValue = (payload: string) => {
    switch (payload) {
      case "DISCONNECT SUCCESS":
        return "Disconnect";
      case "CONNECT SUCCESS":
        return "Connect";
      case "DISCONNECT IN PROGRESS":
        return "Disconnect In Progress";
      case "CONNECT IN PROGRESS":
        return "Connect In Progress";
      default:
        return null;
    }
  };

  const handleSearch = async (searchId?: string) => {
    try {
      dispatch(setIsMeterLoading(true));
      const idToSearch = searchId || meterId || location?.state;

      if (!idToSearch) {
        dispatch(setIsMeterLoading(false));
        return;
      }

      // Step 1: Fetch Meter Details (V2)
      const meterResponse = await fetchGETRequest<any>(`v2/Meters/${idToSearch}`);
      if (!meterResponse?.success || !meterResponse?.data) {
        setToast({ message: "Meter not found", severity: "error" });
        dispatch(setIsMeterLoading(false));
        return;
      }
      const meterData = meterResponse.data;
      dispatch(setMeterV2Details(meterData));
      // For backward compatibility with existing components
      dispatch(
        setMeterDetails({
          name: meterData.meterNumber,
          additionalInfo: {
            SerialNumber: meterData.meterNumber,
            status: meterData.isActive ? "Active" : "Inactive",
            meterCommodityType: meterData.meterType,
            InstallationDate: meterData.installationDate,
          },
        })
      );

      // Step 2: Fetch Site Details (V2)
      if (meterData.siteId) {
        const siteResponse = await fetchGETRequest<any>(`v2/Sites/${meterData.siteId}`);
        if (siteResponse?.success && siteResponse?.data) {
          const siteData = siteResponse.data;
          dispatch(setSiteV2Details(siteData));

          // Step 3: Fetch Contract Details (V2)
          if (siteData.contractId) {
            const contractResponse = await fetchGETRequest<any>(
              `v2/Contracts/${siteData.contractId}`
            );
            if (contractResponse?.success && contractResponse?.data) {
              const contractData = contractResponse.data;
              dispatch(setContractV2Details(contractData));

              // Step 4: Fetch Customer Details (V2)
              if (contractData.customerId) {
                const customerResponse = await fetchGETRequest<any>(
                  `v2/Customers/${contractData.customerId}`
                );
                if (customerResponse?.success && customerResponse?.data) {
                  const customerData = customerResponse.data;
                  dispatch(setCustomerV2Details(customerData));
                  dispatch(setCustomerId(customerData.id));
                  
                  // For backward compatibility
                  dispatch(
                    setCustomerDetails({
                      name: customerData.name,
                      phone: customerData.phone,
                      city: customerData.city,
                      state: customerData.state,
                      country: customerData.country,
                      additionalInfo: {
                        location: customerData.address,
                        registerContactNumber: customerData.phone,
                      },
                    })
                  );
                }
              }
            }
          }
        }
      }

      // Connection Status (Optional if V2 isConnected is sufficient)
      // Keeping it for now if you still want the specialized status
      const responseSecond = await fetchGETRequest<any>(
        `devices/${idToSearch}/connectionstatus`
      );
      if (responseSecond) {
        const chipStatus = getChipValue(
          responseSecond?.Status?.trim().toUpperCase()
        );
        if (chipStatus) {
          setChip(chipStatus);
        }
      } else {
        setChip(meterData.isConnected ? "Connect" : "Disconnect");
      }

      // Step 5: Fetch Latest Instant Telemetry
      const telemetryResponse = await fetchGETRequest<InstantTelemetryV2Response>(
        `v2/meter-telemetry/instant/latest?meterSerial=${meterData.meterNumber}`
      );
      if (telemetryResponse?.success && telemetryResponse?.data) {
        dispatch(setInstantTelemetryV2(telemetryResponse.data));
      }

      dispatch(setHasMeterSearched(true));
      dispatch(setIsMeterLoading(false));
    } catch (error: any) {
      dispatch(setIsMeterLoading(false));
      setToast({ message: error.message, severity: "error" });
    }
  };

  return (
    <Box className="meter">
      <Grid container spacing={matches ? 1 : 2.25}>
        <CustomToast
          open={toast?.message}
          severity={toast?.severity}
          setToast={setToast}
        />

        {/* {!hasMeterSearched && (
          <Grid
            size={{ xs: 12, md: 12, lg: 12 }}
            container
            alignItems={"center"}>
            <Card
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                padding: "14px",
              }}>
              <Typography variant="body1">Meter ID </Typography>
              <CustomSearch
                value={meterId}
                onChange={(e) => dispatch(setMeterId(e.target.value))}
                onClick={handleSearch}
                sx={{ width: "500px" }}
                placeholder={"Search with meter Id"}
              />
            </Card>
          </Grid>
        )} */}
        {hasMeterSearched && !isMeterLoading && (
          <Grid size={{ xs: 12, md: 12, lg: 12 }}>
            <MeterDetailLeftCard handleSearch={handleSearch} chipValue={chip} />
          </Grid>
        )}
        {/* {hasMeterSearched && !isMeterLoading && (
          <Grid size={{ xs: 12, md: 4.5, lg: 4.5 }}>
            <MeterDetailRightCard meterRightCardData={meterRightCardData} />
          </Grid>
        )} */}
      </Grid>
    </Box>
  );
};

export default MeterHeader;
